import { AgentInfo } from './reports.external';
import { MediaGroup } from './workflow';

export type TimeStamp = number;

export const AgentStates = ['Busy', 'Break', 'Idle', 'LoggedOut'] as const;
export type AgentState = typeof AgentStates[number];

export const MediaTypes = ['Voice', 'Email', 'Messaging', 'Webchat'] as const;
export type MediaType = typeof MediaTypes[number];

export type ChannelType = 'voice' | 'email' | 'facebook' | 'sms' | 'webchat' | 'whatsapp' | string;
export type WorkItemState = 'Active' | 'AfterWork' | 'Closed' | 'Lookup' | 'Preview';

export enum VirtualConversationType {
    Preview = 'Preview',
    Callback = 'Callback',
}

export interface IBaseReport {
    readonly id: string;
    name: string;
}

export type PartialReport<T extends IBaseReport> = { id: IBaseReport['id'] } & Partial<T>;
export type ReportDictionary<T extends { id: IBaseReport['id'] }> = Record<IBaseReport['id'], T>;

export interface WorkItemHandlingStats {
    handled: number;
    missed: number;
    avgHandlingTime: number;
}

export interface IQueueReport extends IBaseReport {
    workflowId: string | null;
    mediaGroup: MediaGroup | null;
    slaThreshold: number | null;
    abandonThreshold: number | null;
    startOfToday: TimeStamp | null;
    lwtStart: TimeStamp | null;
    acceptedCount: number;
    abandonedCount: number;
    callbackRequestedCount: number;
    callbackAcceptedCount: number;
    callbackWaitingCount: number;
    slaRatio: number;
    longestWaitTimeInSeconds: number;
    avgWaitingTimeInSeconds: number;
    queueTimedOutCount: number;
    conversationsWaiting: IWaitingConversation[];
    conversationsConnected: IConnectedConversation[];
}

export interface IAgentReport extends IBaseReport {
    readonly tenantId: string;
    currentState: AgentState;
    breakName: string | null;
    currentStateSince: number;
    timeSpentInStates: Record<AgentState, number>;
    timeSpentInBreakNames: Record<string, number>;
    workItemsByMedia: Record<MediaType, WorkItemHandlingStats>;
    workItemsByDispositionCode: Record<string, number>;
    currentlyHandledWorkItems: IHandledWorkItem[];
    loggedInTimestamp: number | null;
    loggedOutTimestamp: number | null;
}

export interface ITeamReport extends IBaseReport {
    readonly memberIds: string[];
    memberReports: IAgentReport[];
    currentMemberAvailability: Record<AgentState, number>;
    timeSpentInStates: Record<AgentState, number>;
    timeSpentInBreakNames: Record<string, number>;
    workItemsByMedia: Record<MediaType, WorkItemHandlingStats>;
    workItemsByDispositionCode: Record<string, number>;
}

export interface IHandledWorkItem {
    primaryConversationId: string | null;
    channelType: ChannelType | WorkItemState;
    channelName: string;
    virtualConversationType: VirtualConversationType | null;
    createdAtTimestamp: number;
    customerName: string;
    customerIdentifier: string;
    state: string;
    isOutbound: boolean | null;
}

interface IConversationBase {
    virtualConversationType: VirtualConversationType | null;
    customer: ICustomerInfo | null;
}

export interface IWaitingConversation extends IConversationBase {
    waitStartTime: TimeStamp;
    position: number | null;
}

export interface IConnectedConversation extends IConversationBase {
    connectionStartTime: TimeStamp;
    agents: AgentInfo[];
}

export interface ICustomerInfo {
    id: string;
    name: string;
    identifier: string;
}

export interface IBotReport extends IBaseReport {
    activeCount: number;
    failedCount: number;
    abandonedCount: number;
    successCount: number;
    successRatio: number;
}

export interface IChatConsumptionReport extends IBaseReport {
    channelType: string;
    createDate: TimeStamp;
    agentMessages: number;
    customerMessages: number;
    systemMessages: number;
}

// default values
export const defaultWorkItemHandlingStats = (): WorkItemHandlingStats => ({
    handled: 0,
    missed: 0,
    avgHandlingTime: 0,
});

export const defaultQueueReport = (id: string): IQueueReport => ({
    id,
    name: 'Loading...',
    workflowId: null,
    mediaGroup: null,
    slaThreshold: null,
    abandonThreshold: null,
    startOfToday: null,
    lwtStart: null,
    acceptedCount: 0,
    abandonedCount: 0,
    callbackRequestedCount: 0,
    callbackAcceptedCount: 0,
    callbackWaitingCount: 0,
    slaRatio: 0,
    longestWaitTimeInSeconds: 0,
    avgWaitingTimeInSeconds: 0,
    queueTimedOutCount: 0,
    conversationsWaiting: [],
    conversationsConnected: [],
});

export const defaultAgentReport = (id: string): IAgentReport => ({
    id,
    tenantId: '',
    name: '',
    currentState: 'LoggedOut',
    breakName: null,
    currentStateSince: 0,
    timeSpentInStates: AgentStates.reduce((coll, state) => ({ ...coll, [state]: 0 }), {} as IAgentReport['timeSpentInStates']),
    timeSpentInBreakNames: {},
    workItemsByMedia: MediaTypes.reduce((coll, media) => ({ ...coll, [media]: { handled: 0, missed: 0, avgHandlingTime: 0 } }), {} as IAgentReport['workItemsByMedia']),
    workItemsByDispositionCode: {},
    currentlyHandledWorkItems: [],
    loggedInTimestamp: null,
    loggedOutTimestamp: null,
});

export const defaultBotReport = (id: string): IBotReport => ({
    id,
    name: 'Loading...',
    abandonedCount: 0,
    activeCount: 0,
    failedCount: 0,
    successCount: 0,
    successRatio: 0,
});